import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { Tabs, DatePicker, ConfigProvider, TimeRangePickerProps, DatePickerProps } from 'antd';
import CountUp from 'react-countup';
import { CircularProgress, Skeleton } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import locale from 'antd/locale/pt_BR';
import { useTheme } from 'styled-components';
import { NavHeader } from '../../components/NavHeader';
import { BasicButton } from '../../components';
import {
  DashboardContainer,
  FiltersContainer,
  StatisticsWrapper,
  MainStats,
  StatBox,
  SideStats,
  SideStatBox,
  Header,
} from './styles';

import {
  requestCorrespondentsReport,
  requestUserMovementsReport,
  requestIncompleteRegistersReport,
} from '../../controller/ReportController';
import { UserController } from '../../controller/UserController';
import { useAlert } from '../../hooks/useAlert';
import { RangePickerProps } from 'antd/es/date-picker';
import TabSolicitationDashboard from '../../components/Dashboard/TabSolicitationDashboard';
import { IUserStatisticsViewModel } from 'viewModels';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const filterSchema = yup.object().shape({
  dateRange: yup
    .array()
    .of(yup.date().required('Data obrigatória'))
    .min(2, 'Selecione o intervalo completo')
    .required('Intervalo de datas é obrigatório'),
});

export function Dashboard() {
  const { colors } = useTheme();
  const alert = useAlert();

  const getMonday = (date: dayjs.Dayjs) => {
    const d = dayjs(date);
    return d.startOf('week').add(1, 'day');
  };

  const today = dayjs();
  const currentMonday = getMonday(today);
  const lastMonday = currentMonday.subtract(7, 'days');

  const [startUserMovementsDate, setStartUserMovementsDate] = useState(dayjs().subtract(1, 'week'));
  const [endUserMovementsDate, setEndUserMovementsDate] = useState(dayjs().endOf('day'));
  const [startIncompleteDate, setStartIncompleteDate] = useState(dayjs().subtract(1, 'week'));
  const [endIncompleteDate, setEndIncompleteDate] = useState(dayjs().endOf('day'));
  const [dateRange, setDateRange] = useState<RangePickerProps['value']>([lastMonday, currentMonday]);
  const [activeTab, setActiveTab] = useState('allUsers');
  const [dashboardStats, setDashboardStats] = useState({
    totalUsers: 0,
    totalActiveUsers: 0,
    totalInactiveUsers: 0,
    totalInvites: 0,
    totalActivatedLastWeek: 0,
  });
  const [loadingCorrespondentStatistics, setLoadingCorrespondentStatistics] = useState(false);
  const [loadingUserStatistics, setLoadingUserStatistics] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);

  const [allStats, setAllStats] = useState<IUserStatisticsViewModel[]>([]);

  const { setError } = useForm({
    defaultValues: { dateRange: null },
    resolver: yupResolver(filterSchema),
  });

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const exportCorrespondentsXlsx = async () => {
    if (!dateRange || dateRange.length < 2 || !dateRange[0] || !dateRange[1]) {
      setError('dateRange', { message: 'Selecione um intervalo completo' });
      return;
    }
    setLoadingReport(true);
    const [startDate, endDate] = dateRange;
    if (!startDate || !endDate) {
      setError('dateRange', { message: 'Selecione um intervalo completo' });
      setLoadingReport(false);
      return;
    }
    requestCorrespondentsReport(startDate.toDate(), endDate.toDate()) 
      .then(() => {
        alert.show({ message: 'Relatório solicitado com sucesso. Você receberá uma notificação quando estiver pronto.', type: 'success' });
      })
      .catch(() => {
        alert.show({ message: 'Erro ao solicitar relatório', type: 'error' });
      });
  };

  const handleAllDateChange = (dates: DatePickerProps['value'] | RangePickerProps['value']) => {
    if (Array.isArray(dates)) {
      const [start, end] = dates;
      if (start && end) {
        setStartUserMovementsDate(dayjs(start));
        setEndUserMovementsDate(dayjs(end).endOf('day'));
      }
    } else if (dates) {
      setStartUserMovementsDate(dayjs(dates));
    } else {
      setEndUserMovementsDate(dayjs());
    }
  };

  const exportAllUsersXlsx = async () => {
    setLoadingReport(true);
    requestUserMovementsReport(startUserMovementsDate.toDate(), endUserMovementsDate.toDate())
      .then(() => {
        alert.show({ message: 'Relatório solicitado com sucesso. Você receberá uma notificação quando estiver pronto.', type: 'success' });
      })
      .catch(() => {
        alert.show({ message: 'Erro ao solicitar relatório', type: 'error' });
      })
      .finally(() => {
        setLoadingReport(false);
      });
  };

  const handleIncompleteDateChange = (dates: DatePickerProps['value'] | RangePickerProps['value']) => {
    if (Array.isArray(dates)) {
      const [start, end] = dates;
      if (start && end) {
        setStartIncompleteDate(dayjs(start));
        setEndIncompleteDate(dayjs(end).endOf('day'));
      }
    } else if (dates) {
      setStartIncompleteDate(dayjs(dates));
    } else {
      setEndIncompleteDate(dayjs());
    }
  };

  const exportIncompleteUsersXlsx = async () => {
    setLoadingReport(true);
    requestIncompleteRegistersReport(startIncompleteDate.toDate(), endIncompleteDate.toDate())
      .then(() => {
        alert.show({ message: 'Relatório solicitado com sucesso. Você receberá uma notificação quando estiver pronto.', type: 'success' });
      })
      .catch(() => {
        alert.show({ message: 'Erro ao solicitar relatório', type: 'error' });
      })
      .finally(() => {
        setLoadingReport(false);
      });
  };

  const rangePresets: TimeRangePickerProps['presets'] = [
    { label: 'Últimos 7 Dias', value: [dayjs().subtract(7, 'days'), dayjs().endOf('day')] },
    { label: 'Últimos 14 Dias', value: [dayjs().subtract(14, 'days'), dayjs().endOf('day')] },
    { label: 'Último mês', value: [dayjs().subtract(1, 'month'), dayjs().endOf('day')] },
    { label: 'Últimos 3 meses', value: [dayjs().subtract(3, 'months'), dayjs().endOf('day')] },
  ];

  useEffect(() => {
    setLoadingCorrespondentStatistics(true);
    UserController.getDashboardStatistics()
      .then((response) => {
        setDashboardStats(response);
      })
      .catch(() => {
        alert.show({ message: 'Erro ao buscar estatísticas', type: 'error' });
      })
      .finally(() => {
        setLoadingCorrespondentStatistics(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoadingUserStatistics(true);
    UserController.getUserStatistics(startUserMovementsDate, endUserMovementsDate)
      .then((response) => {
        setAllStats(response);
      })
      .catch(() => {
        alert.show({ message: 'Erro ao buscar estatísticas', type: 'error' });
      })
      .finally(() => {
        setLoadingUserStatistics(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startUserMovementsDate, endUserMovementsDate]);

  return (
    <ConfigProvider
      locale={locale}
      theme={{
        token: {
          colorPrimary: colors.primary,
        },
      }}>
      <NavHeader typeHeader="company" />
      <DashboardContainer>
        <Header>
          <h1>DashBoard</h1>
        </Header>

        <Tabs defaultActiveKey="allUsers" activeKey={activeTab} onChange={handleTabChange}>
          <TabPane tab="Todos os usuários" key="allUsers">
            <FiltersContainer>
              <RangePicker
                value={[startUserMovementsDate, endUserMovementsDate]}
                presets={rangePresets}
                onChange={handleAllDateChange}
                maxDate={dayjs()}
                minDate={dayjs().subtract(100, 'days')}
                format="DD/MM/YYYY"
              />
              <BasicButton
                disabled={loadingReport}
                variant="contained"
                size="large"
                onClick={exportAllUsersXlsx}>
                {loadingReport ? (
                  <div className="button-text">
                    Extraindo <CircularProgress style={{ color: 'white' }} size="15px" />
                  </div>
                ) : (
                  <span>Extrair Relatório</span>
                )}
              </BasicButton>
            </FiltersContainer>
            {allStats.map((stat) => (
              <StatisticsWrapper>
                <span>{stat.host.includes('adv') ? 'Legal Hub Adv' : 'Legal Hub Vigna'}</span>
                <MainStats
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '20px',
                    width: '100%',
                  }}>
                  <StatBox>
                    <span>Número de Registros</span>
                    <span>
                      {loadingUserStatistics ? (
                        <Skeleton variant="text" width={80} />
                      ) : (
                        <CountUp duration={2} end={stat.totalRecords} />
                      )}
                    </span>
                  </StatBox>
                  <StatBox>
                    <span style={{ whiteSpace: 'nowrap' }}>Total de Movimentações</span>
                    <span>
                      {loadingUserStatistics ? (
                        <Skeleton variant="text" width={80} />
                      ) : (
                        <CountUp duration={2} end={stat.totalMovements} />
                      )}
                    </span>
                  </StatBox>
                </MainStats>
              </StatisticsWrapper>
            ))}
          </TabPane>

          <TabPane tab="Cadastros incompletos" key="incompleteUsers">
            <FiltersContainer>
              <RangePicker
                value={[startIncompleteDate, endIncompleteDate]}
                presets={rangePresets}
                onChange={handleIncompleteDateChange}
                maxDate={dayjs()}
                format="DD/MM/YYYY"
              />
              <BasicButton
                disabled={loadingReport}
                variant="contained"
                size="large"
                onClick={exportIncompleteUsersXlsx}>
                {loadingReport ? (
                  <div className="button-text">
                    Extraindo <CircularProgress style={{ color: 'white' }} size="15px" />
                  </div>
                ) : (
                  <span>Extrair todos incompletos</span>
                )}
              </BasicButton>
            </FiltersContainer>
          </TabPane>

          <TabPane tab="Correspondentes" key="correspondentes">
            <FiltersContainer>
              <RangePicker
                presets={rangePresets}
                defaultValue={[lastMonday, currentMonday]}
                value={dateRange ?? null}
                onChange={setDateRange}
                format="DD/MM/YYYY"
              />
              <BasicButton
                variant="contained"
                size="large"
                onClick={exportCorrespondentsXlsx}>
                Extrair Relatório
              </BasicButton>
            </FiltersContainer>

            <StatisticsWrapper>
              <MainStats>
                <StatBox>
                  <span>Total Ativos</span>
                  {loadingCorrespondentStatistics ? (
                    <Skeleton variant="text" width={80} />
                  ) : (
                    <span>{dashboardStats.totalActiveUsers}</span>
                  )}
                </StatBox>
                <StatBox>
                  <span>Total Base</span>
                  {loadingCorrespondentStatistics ? (
                    <Skeleton variant="text" width={80} />
                  ) : (
                    <span>{dashboardStats.totalUsers}</span>
                  )}
                </StatBox>
                <StatBox>
                  <span>Total de Indicações</span>
                  {loadingCorrespondentStatistics ? (
                    <Skeleton variant="text" width={80} />
                  ) : (
                    <span>{dashboardStats.totalInvites}</span>
                  )}
                </StatBox>
              </MainStats>

              <SideStats>
                <SideStatBox>
                  <span className="date">
                    {`De ${lastMonday.format('DD/MM/YYYY')} a ${currentMonday.format(
                      'DD/MM/YYYY',
                    )}`}
                  </span>
                  {loadingCorrespondentStatistics ? (
                    <Skeleton variant="text" width={80} />
                  ) : (
                    <span>{dashboardStats.totalActivatedLastWeek}</span>
                  )}
                  <span>USUÁRIOS ATIVOS</span>
                </SideStatBox>
                <SideStatBox className="inactive">
                  <span className="date">{`Até ${today.format('DD/MM/YYYY')}`}</span>
                  {loadingCorrespondentStatistics ? (
                    <Skeleton variant="text" width={80} />
                  ) : (
                    <span>{dashboardStats.totalInactiveUsers}</span>
                  )}
                  <span>USUÁRIOS INATIVOS</span>
                </SideStatBox>
              </SideStats>
            </StatisticsWrapper>
          </TabPane>

          <TabPane tab="Solicitações" key="solicitations">
            <TabSolicitationDashboard />
          </TabPane>
        </Tabs>
      </DashboardContainer>
    </ConfigProvider>
  );
}
